import React, { useState, useRef, useEffect, useCallback } from "react";

import { Button } from "../ui/button";
import Input from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

import { imageUploadAWS } from "../../utils/images";
import { generateRandomUUID } from "../../utils/general";
import { todayISODate } from "../../lib/utils";

import { useToast } from "../../hooks/use-toast";

import Loader from "../ui/loader";

import {
  useAddVehicle,
  useUpdateVehicle,
} from "../../queries/vehicles.queries";

// Types
import {
  CarFormData,
  TransmissionType,
  FuelType,
  CarCondition,
  CarCreate,
  VehicleStatus,
  UpdateCar,
} from "../../types";

interface SaleFormData {
  status: VehicleStatus;
  price_sold: string;
  sold_at: string;
}

const getSaleFormData = (vehicle?: CarCreate): SaleFormData => {
  const isSold =
    vehicle?.status === VehicleStatus.SOLD || vehicle?.is_available === false;
  return {
    status: isSold ? VehicleStatus.SOLD : VehicleStatus.AVAILABLE,
    price_sold: vehicle?.price_sold != null ? String(vehicle.price_sold) : "",
    sold_at: vehicle?.sold_at ? vehicle.sold_at.slice(0, 10) : "",
  };
};

export interface AddCarModalPropTypes {
  showAddCarForm: boolean;
  setShowAddCarForm: React.Dispatch<React.SetStateAction<boolean>>;
  vehicleDetails?: CarCreate;
  isEdit?: boolean;
  setIsEdit?: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddCarModal: React.FC<AddCarModalPropTypes> = ({
  showAddCarForm,
  setShowAddCarForm,
  vehicleDetails,
  isEdit,
  setIsEdit,
}) => {
  const [carForm, setCarForm] = useState<CarFormData>({
    numplate: vehicleDetails?.numplate ?? "",
    make: vehicleDetails?.make ?? "",
    model: vehicleDetails?.model ?? "",
    year: vehicleDetails?.year ?? new Date().getFullYear(),
    price: vehicleDetails?.price ?? 0,
    price_bought: vehicleDetails?.price_bought ?? 0,
    mileage: vehicleDetails?.mileage ?? 0,
    color: vehicleDetails?.color ?? "",
    transmission: vehicleDetails?.transmission ?? TransmissionType.AUTOMATIC,
    fuel_type: vehicleDetails?.fuel_type ?? FuelType.PETROL,
    condition: vehicleDetails?.condition ?? CarCondition.USED,
    cubic_capacity: vehicleDetails?.cubic_capacity ?? "",
    horsepower: vehicleDetails?.horsepower ?? 0,
    images: vehicleDetails?.images ?? [],
    description: vehicleDetails?.description ?? "",
    features: vehicleDetails?.features ?? [],
  });
  const [saleForm, setSaleForm] = useState<SaleFormData>(
    getSaleFormData(vehicleDetails),
  );
  const [files, setFiles] = useState<File[]>([]);
  const [oldImage, setOldImages] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toast } = useToast();

  const addVehicle = useAddVehicle();
  const updateVehicle = useUpdateVehicle();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddImage = (): void => {
    if (fileInputRef.current) {
      fileInputRef.current?.click();
    }
  };

  const handleAddFeature = (): void => {
    setCarForm((prev) => ({
      ...prev,
      features: [...prev.features, ""],
    }));
  };

  const handleFeatureChange = (index: number, value: string): void => {
    setCarForm((prev) => ({
      ...prev,
      features: prev.features.map((feature, i) =>
        i === index ? value : feature,
      ),
    }));
  };

  const handleCarFormChange = (
    field: keyof CarFormData,
    value: string | number,
  ): void => {
    setCarForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setIsUploading(true);
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles((prevFiles) => [...prevFiles, ...newFiles]);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    setIsUploading(false);
  };

  const handleRemoveFile = (index: number): void => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveImage = (index: number, imageUrl: string): void => {
    setCarForm((prev) => ({
      ...prev,
      images: (prev.images ?? []).filter((_, i) => i !== index),
    }));
    setOldImages((prevFiles) => [...prevFiles, imageUrl]);
  };

  const resetForm = useCallback(() => {
    setCarForm({
      numplate: "",
      make: "",
      model: "",
      year: new Date().getFullYear(),
      price: 0,
      price_bought: 0,
      mileage: 0,
      color: "",
      transmission: TransmissionType.AUTOMATIC,
      fuel_type: FuelType.PETROL,
      condition: CarCondition.USED,
      cubic_capacity: "",
      horsepower: 0,
      images: [],
      description: "",
      features: [],
    });
    setFiles([]);
    setOldImages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleSubmitVehicle = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();

    if (files.length === 0 && carForm.images.length === 0) {
      toast({
        title: "Error",
        description: "At least one image is required",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);

      let uploadedUrls: string[] = [];

      if (files.length > 0) {
        uploadedUrls = await imageUploadAWS(files);
      }

      const finalImageUrls = [
        ...carForm.images.filter(Boolean),
        ...uploadedUrls,
      ];

      if (finalImageUrls.length === 0) {
        setIsSubmitting(false);
        toast({
          title: "Error",
          description: "At least one image is required",
          variant: "destructive",
        });
        return;
      }

      const filteredFeatures = carForm.features.filter(
        (feature) => feature.trim() !== "",
      );

      if (!isEdit) {
        const carData: CarCreate = {
          ...carForm,
          id: generateRandomUUID(),
          images: finalImageUrls,
          description: carForm.description,
          features: filteredFeatures,
          is_available: true,
          status: VehicleStatus.AVAILABLE,
        };

        await addVehicle.mutateAsync(carData);
      } else {
        if (!vehicleDetails?.id) {
          setIsSubmitting(false);
          toast({
            title: "Error",
            description: "Invalid vehicle data",
            variant: "destructive",
          });
          return;
        }
        const id = vehicleDetails.id;
        const isSold = saleForm.status === VehicleStatus.SOLD;
        const priceSold = parseInt(saleForm.price_sold);
        const carData: UpdateCar = {
          imagesToRemoveUrls: oldImage,
          updatedVehicleFields: {
            ...carForm,
            id,
            images: finalImageUrls,
            features: filteredFeatures,
            status: saleForm.status,
            price_sold: isSold && Number.isFinite(priceSold) ? priceSold : null,
            sold_at: isSold && saleForm.sold_at ? saleForm.sold_at : undefined,
          },
        };

        await updateVehicle.mutateAsync(carData);
      }

      setIsSubmitting(false);

      if (isEdit) {
        toast({
          title: "Success!",
          description: "Vehicle updated successfully",
        });
      } else {
        toast({
          title: "Success!",
          description: "Vehicle added successfully",
        });
      }

      setShowAddCarForm(false);
      setIsEdit?.(false);
      resetForm();
    } catch (error) {
      setShowAddCarForm(false);
      setIsEdit?.(false);
      setIsSubmitting(false);

      toast({
        title: "Error",
        description: "Failed to add vehicle",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    setCarForm({
      numplate: vehicleDetails?.numplate ?? "",
      make: vehicleDetails?.make ?? "",
      model: vehicleDetails?.model ?? "",
      year: vehicleDetails?.year ?? new Date().getFullYear(),
      price: vehicleDetails?.price ?? 0,
      price_bought: vehicleDetails?.price_bought ?? 0,
      mileage: vehicleDetails?.mileage ?? 0,
      color: vehicleDetails?.color ?? "",
      transmission: vehicleDetails?.transmission ?? TransmissionType.AUTOMATIC,
      fuel_type: vehicleDetails?.fuel_type ?? FuelType.PETROL,
      condition: vehicleDetails?.condition ?? CarCondition.USED,
      cubic_capacity: vehicleDetails?.cubic_capacity ?? "",
      horsepower: vehicleDetails?.horsepower ?? 0,
      images: vehicleDetails?.images ?? [],
      description: vehicleDetails?.description ?? "",
      features: vehicleDetails?.features ?? [""],
    });
  }, [vehicleDetails]);

  useEffect(() => {
    setOldImages([]);
    setFiles([]);
    setSaleForm(getSaleFormData(vehicleDetails));
    setCarForm({
      numplate: vehicleDetails?.numplate ?? "",
      make: vehicleDetails?.make ?? "",
      model: vehicleDetails?.model ?? "",
      year: vehicleDetails?.year ?? new Date().getFullYear(),
      price: vehicleDetails?.price ?? 0,
      price_bought: vehicleDetails?.price_bought ?? 0,
      mileage: vehicleDetails?.mileage ?? 0,
      color: vehicleDetails?.color ?? "",
      transmission: vehicleDetails?.transmission ?? TransmissionType.AUTOMATIC,
      fuel_type: vehicleDetails?.fuel_type ?? FuelType.PETROL,
      condition: vehicleDetails?.condition ?? CarCondition.USED,
      cubic_capacity: vehicleDetails?.cubic_capacity ?? "",
      horsepower: vehicleDetails?.horsepower ?? 0,
      images: vehicleDetails?.images ?? [],
      description: vehicleDetails?.description ?? "",
      features: vehicleDetails?.features ?? [],
    });
  }, [vehicleDetails]);

  useEffect(() => {
    if (showAddCarForm && !isEdit) {
      resetForm();
    }
  }, [showAddCarForm, isEdit, resetForm]);

  const handleOnCancel = () => {
    setIsEdit?.(false);
    setShowAddCarForm(false);
  };

  return (
    <Dialog open={showAddCarForm} onOpenChange={setShowAddCarForm}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Update Vehicle" : "Add New Vehicle"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update the details for the vehicle"
              : "Fill in the details for the new vehicle listing"}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmitVehicle} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Registration Number *</Label>
              <Input
                value={carForm.numplate}
                onChange={(e) =>
                  handleCarFormChange("numplate", e.target.value)
                }
                placeholder="e.g. WTH 1324"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Make *</Label>
              <Input
                value={carForm.make}
                onChange={(e) => handleCarFormChange("make", e.target.value)}
                placeholder="e.g. Toyota"
                required
              />
            </div>
            <div>
              <Label>Model *</Label>
              <Input
                value={carForm.model}
                onChange={(e) => handleCarFormChange("model", e.target.value)}
                placeholder="e.g. Vios"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Price *</Label>
              <Input
                type="number"
                value={carForm.price}
                onChange={(e) =>
                  handleCarFormChange("price", parseInt(e.target.value))
                }
                placeholder="50000"
                required
              />
            </div>
            <div>
              <Label>Price Bought *</Label>
              <Input
                type="number"
                value={carForm.price_bought}
                onChange={(e) =>
                  handleCarFormChange("price_bought", parseInt(e.target.value))
                }
                placeholder="50000"
                required
              />
            </div>
          </div>

          {isEdit && (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Status</Label>
                <Select
                  value={saleForm.status}
                  onValueChange={(value) =>
                    setSaleForm((prev) => ({
                      ...prev,
                      status: value as VehicleStatus,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={VehicleStatus.AVAILABLE}>
                      Available
                    </SelectItem>
                    <SelectItem value={VehicleStatus.SOLD}>Sold</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>
                  Price Sold {saleForm.status === VehicleStatus.SOLD && "*"}
                </Label>
                <Input
                  type="number"
                  min="0"
                  value={saleForm.price_sold}
                  onChange={(e) =>
                    setSaleForm((prev) => ({
                      ...prev,
                      price_sold: e.target.value,
                    }))
                  }
                  placeholder="55000"
                  disabled={saleForm.status !== VehicleStatus.SOLD}
                  required={saleForm.status === VehicleStatus.SOLD}
                />
              </div>
              <div>
                <Label>Sold Date</Label>
                <Input
                  type="date"
                  value={saleForm.sold_at}
                  max={todayISODate()}
                  onChange={(e) =>
                    setSaleForm((prev) => ({
                      ...prev,
                      sold_at: e.target.value,
                    }))
                  }
                  disabled={saleForm.status !== VehicleStatus.SOLD}
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Year *</Label>
              <Input
                type="number"
                value={carForm.year}
                onChange={(e) =>
                  handleCarFormChange("year", parseInt(e.target.value))
                }
                required
              />
            </div>
            <div>
              <Label>Mileage *</Label>
              <Input
                type="number"
                value={carForm.mileage}
                onChange={(e) =>
                  handleCarFormChange("mileage", parseInt(e.target.value))
                }
                placeholder="25000"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Color *</Label>
              <Input
                value={carForm.color}
                onChange={(e) => handleCarFormChange("color", e.target.value)}
                placeholder="e.g. Black"
                required
              />
            </div>
            <div>
              <Label>Engine Size *</Label>
              <Input
                value={carForm.cubic_capacity}
                onChange={(e) =>
                  handleCarFormChange("cubic_capacity", e.target.value)
                }
                placeholder="e.g. 1500cc"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label>Transmission</Label>
              <Select
                value={carForm.transmission}
                onValueChange={(value) =>
                  handleCarFormChange("transmission", value as TransmissionType)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TransmissionType.AUTOMATIC}>
                    Automatic
                  </SelectItem>
                  <SelectItem value={TransmissionType.MANUAL}>
                    Manual
                  </SelectItem>
                  <SelectItem value={TransmissionType.CVT}>CVT</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fuel Type</Label>
              <Select
                value={carForm.fuel_type}
                onValueChange={(value) =>
                  handleCarFormChange("fuel_type", value as FuelType)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={FuelType.PETROL}>Petrol</SelectItem>
                  <SelectItem value={FuelType.DIESEL}>Diesel</SelectItem>
                  <SelectItem value={FuelType.ELECTRIC}>Electric</SelectItem>
                  <SelectItem value={FuelType.HYBRID}>Hybrid</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Condition</Label>
              <Select
                value={carForm.condition}
                onValueChange={(value) =>
                  handleCarFormChange("condition", value as CarCondition)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={CarCondition.NEW}>New</SelectItem>
                  <SelectItem value={CarCondition.USED}>Used</SelectItem>
                  <SelectItem value={CarCondition.CERTIFIED_PRE_OWNED}>
                    Certified Pre-Owned
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>Horsepower *</Label>
            <Input
              type="number"
              value={carForm.horsepower}
              onChange={(e) =>
                handleCarFormChange("horsepower", parseInt(e.target.value))
              }
              placeholder="300"
              required
            />
          </div>

          <div>
            <Label>Description *</Label>
            <Textarea
              value={carForm.description}
              onChange={(e) =>
                handleCarFormChange("description", e.target.value)
              }
              placeholder="Describe the vehicle's condition, features, and selling points..."
              rows={3}
              required
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>
                Images {files.length > 0 && `(${files.length} selected)`}
              </Label>
              <Button type="button" size="sm" onClick={handleAddImage}>
                Add Image
              </Button>
              <input
                type="file"
                accept="image/*"
                multiple
                ref={fileInputRef}
                onChange={handleFileChange}
                hidden
              />
            </div>
            {/* Custom Preview */}
            {/* Existing Images */}
            {carForm.images.length > 0 && (
              <div className="space-y-2">
                {carForm.images
                  .filter((url) => url.trim() !== "")
                  .map((imageUrl, idx) => {
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <img
                          src={imageUrl}
                          alt={"image"}
                          className="w-16 h-16 object-cover rounded-lg border"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveImage(idx, imageUrl)}
                        >
                          Remove
                        </Button>
                      </div>
                    );
                  })}
              </div>
            )}

            {/* New Images */}
            {files.length > 0 && (
              <div className="space-y-2">
                {files.map((file, idx) => {
                  const isImage = file.type.startsWith("image/");
                  return (
                    <div
                      key={`${file.name}-${file.size}-${idx}`}
                      className="flex items-center gap-3"
                    >
                      {isImage ? (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="w-16 h-16 object-cover rounded-lg border"
                        />
                      ) : (
                        <span className="px-2 py-1 text-sm bg-gray-200 rounded">
                          {file.type || "File"}
                        </span>
                      )}
                      <span className="text-[12px] text-gray-700 flex-1">
                        {file.name.substring(0, 10)}... (
                        {Math.round(file.size / 1024)} KB)
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveFile(idx)}
                      >
                        Remove
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
            {isUploading && <Loader isSmall={true} />}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Features</Label>
              <Button type="button" size="sm" onClick={handleAddFeature}>
                Add Feature
              </Button>
            </div>
            {carForm.features.map((feature, index) => (
              <Input
                key={index}
                value={feature}
                onChange={(e) => handleFeatureChange(index, e.target.value)}
                placeholder="e.g. Android player"
                className="mb-2"
              />
            ))}
          </div>

          <div className="flex space-x-2 pt-4">
            <Button
              type="submit"
              className="flex-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                isEdit ? "Updating..." : "Adding..."
              ) : (
                isEdit ? "Update Vehicle" : "Add Vehicle"
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleOnCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(AddCarModal);
